// Podcast episode page: our own player in place of Buzzsprout's embed.
//
// Grant pastes Buzzsprout's embed into the show notes as usual:
//
//   <div id="buzzsprout-player-14734288"></div><script src="…"></script>
//
// The number in that id is the episode. We play its MP3 directly
// (buzzsprout.com/740051/<id>.mp3, Buzzsprout's "Direct Link to MP3"),
// and look up the episode number and length in the show's public feed.
// A guest spot whose show has no Spotify page can carry the episode's
// audio file instead (Libsyn's "Download" link, or any .mp3 / .m4a):
// pasted as an ordinary link, or as <audio src="…"> in an HTML card. We
// play that file, with no feed lookup. Audio is only requested when someone presses play, so
// page views don't count as downloads. If the audio fails, whatever was
// pasted goes back where it was.
//
// Also draws the decorative waveforms, seeded from each title so every
// episode has its own.

const SHOW = "740051";
const FEED = `https://feeds.buzzsprout.com/${SHOW}.rss`;
const CACHE_KEY = "gg-ep-feed";
const SPEEDS = [1, 1.25, 1.5, 2];

// --- Waveforms -------------------------------------------------------------

function seeded(text) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
    return () => {
        h = Math.imul(h ^ (h >>> 15), h | 1);
        h ^= h + Math.imul(h ^ (h >>> 7), h | 61);
        return ((h ^ (h >>> 14)) >>> 0) / 4294967296;
    };
}

function drawWave(svg, seed, bars) {
    const rand = seeded(seed);
    const w = bars * 4;
    let out = "";
    for (let i = 0; i < bars; i++) {
        const env = Math.pow(Math.sin((Math.PI * i) / bars), 0.6);
        const a = Math.max(2, 92 * env * (0.25 + 0.75 * rand()));
        out += `<rect x="${i * 4}" y="${(50 - a / 2).toFixed(1)}" width="2.4" height="${a.toFixed(1)}" rx="1"/>`;
    }
    svg.setAttribute("viewBox", `0 0 ${w} 100`);
    svg.setAttribute("preserveAspectRatio", "none");
    svg.innerHTML = out;
}

// --- Feed lookup -----------------------------------------------------------

function seconds(text) {
    if (!text) return 0;
    return text.split(":").reduce((total, part) => total * 60 + Number(part), 0);
}

function readCache() {
    try {
        return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || null;
    } catch {
        return null;
    }
}

async function feedIndex() {
    const cached = readCache();
    if (cached) return cached;

    const res = await fetch(FEED);
    if (!res.ok) throw new Error(`feed ${res.status}`);
    const xml = new DOMParser().parseFromString(await res.text(), "text/xml");
    const index = {};
    xml.querySelectorAll("item").forEach((item) => {
        const url = item.querySelector("enclosure")?.getAttribute("url") || "";
        const id = (url.match(/episodes\/(\d+)/) || [])[1];
        if (!id) return;
        const num = ((item.querySelector("title")?.textContent || "").match(/#\s*(\d+)/) || [])[1];
        const dur = item.getElementsByTagName("itunes:duration")[0]?.textContent;
        index[id] = [num ? Number(num) : null, seconds(dur)];
    });
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(index));
    } catch {
        // Private mode or full storage: fine, we just fetch again next time.
    }
    return index;
}

// --- Player ----------------------------------------------------------------

function clock(s) {
    if (!Number.isFinite(s)) return "0:00";
    s = Math.floor(s);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = String(s % 60).padStart(2, "0");
    return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

const ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
const ICON_PAUSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';

function buildPlayer(root, src, title, album) {
    root.innerHTML = `
        <button class="gg-ep__play" type="button" aria-label="Play episode">${ICON_PLAY}</button>
        <div class="gg-ep__track">
            <div class="gg-ep__track-top">
                <span class="gg-ep__time" aria-live="off"><span data-t>0:00</span> / <span data-d>–:––</span></span>
                <span class="gg-ep__controls">
                    <button type="button" data-skip="-15" aria-label="Back 15 seconds">−15</button>
                    <button type="button" data-skip="30" aria-label="Forward 30 seconds">+30</button>
                    <button type="button" data-speed aria-label="Playback speed">1×</button>
                </span>
            </div>
            <div class="gg-ep__scrub">
                <svg class="gg-ep__scrub-wave" aria-hidden="true"></svg>
                <div class="gg-ep__scrub-fill" aria-hidden="true"><svg class="gg-ep__scrub-wave"></svg></div>
                <input class="gg-ep__seek" type="range" min="0" max="1000" value="0" step="1" aria-label="Seek">
            </div>
        </div>`;
    root.querySelectorAll(".gg-ep__scrub-wave").forEach((svg) => drawWave(svg, title, 150));

    const play = root.querySelector(".gg-ep__play");
    const seek = root.querySelector(".gg-ep__seek");
    const fill = root.querySelector(".gg-ep__scrub-fill");
    const tEl = root.querySelector("[data-t]");
    const dEl = root.querySelector("[data-d]");
    const speedBtn = root.querySelector("[data-speed]");
    let audio = null;
    let known = 0;

    const setDuration = (d) => {
        if (!d) return;
        known = d;
        dEl.textContent = clock(d);
    };

    const paint = () => {
        const d = (audio && Number.isFinite(audio.duration) && audio.duration) || known;
        const t = audio ? audio.currentTime : 0;
        const p = d ? t / d : 0;
        fill.style.setProperty("--p", `${p * 100}%`);
        seek.value = String(Math.round(p * 1000));
        seek.setAttribute("aria-valuetext", `${clock(t)} of ${clock(d)}`);
        tEl.textContent = clock(t);
    };

    const ensureAudio = () => {
        if (audio) return audio;
        audio = new Audio();
        audio.preload = "none";
        audio.src = src;
        audio.addEventListener("timeupdate", paint);
        audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
        audio.addEventListener("play", () => {
            play.innerHTML = ICON_PAUSE;
            play.setAttribute("aria-label", "Pause episode");
            root.classList.add("is-playing");
        });
        audio.addEventListener("pause", () => {
            play.innerHTML = ICON_PLAY;
            play.setAttribute("aria-label", "Play episode");
            root.classList.remove("is-playing");
        });
        audio.addEventListener("error", () => root.dispatchEvent(new CustomEvent("ep:fail")));
        if ("mediaSession" in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({ title, artist: "Grant Giles", album });
        }
        return audio;
    };

    play.addEventListener("click", () => {
        const a = ensureAudio();
        if (a.paused) a.play().catch(() => root.dispatchEvent(new CustomEvent("ep:fail")));
        else a.pause();
    });

    root.querySelectorAll("[data-skip]").forEach((btn) =>
        btn.addEventListener("click", () => {
            const a = ensureAudio();
            a.currentTime = Math.max(0, a.currentTime + Number(btn.dataset.skip));
            paint();
        })
    );

    speedBtn.addEventListener("click", () => {
        const a = ensureAudio();
        const next = SPEEDS[(SPEEDS.indexOf(a.playbackRate) + 1) % SPEEDS.length];
        a.playbackRate = next;
        speedBtn.textContent = `${next}×`;
    });

    seek.addEventListener("input", () => {
        const a = ensureAudio();
        const d = (Number.isFinite(a.duration) && a.duration) || known;
        if (d) a.currentTime = (Number(seek.value) / 1000) * d;
        paint();
    });

    return { setDuration };
}

// Drops the pasted "LISTEN ON Apple Podcasts, Spotify, Google Podcasts"
// line: the band has its own app links. Some posts run the notes on in
// the same paragraph after a double <br>, so only that first part goes.
function dropListenLine(notes) {
    const p = [...notes.querySelectorAll("p")].find((el) => /^\s*listen on\b/i.test(el.textContent));
    if (!p) return;
    const parts = p.innerHTML.split(/<br\s*\/?>\s*<br\s*\/?>/i);
    const rest = parts.slice(1).join("<br><br>").trim();
    if (rest) p.innerHTML = rest;
    else p.remove();
}

// PodcastEpisode structured data, from the page's data-ep-* attributes
// plus what the feed told us. Guest spots belong to other shows: none.
function writeSchema(page, { id, num, dur } = {}) {
    if (page.hasAttribute("data-ep-guest") || document.querySelector("script[data-ep-schema]")) return;
    const d = page.dataset;
    const data = {
        "@context": "https://schema.org",
        "@type": "PodcastEpisode",
        name: d.epTitle,
        url: d.epUrl,
        datePublished: d.epDate,
        description: d.epDescription || undefined,
        image: d.epImage || undefined,
        episodeNumber: num || undefined,
        duration: dur ? `PT${Math.floor(dur / 60)}M${dur % 60}S` : undefined,
        associatedMedia: id ? { "@type": "MediaObject", contentUrl: `https://www.buzzsprout.com/${SHOW}/${id}.mp3` } : undefined,
        partOfSeries: {
            "@type": "PodcastSeries",
            name: "The Roaring Heads",
            url: `${location.origin}/podcast/`,
            webFeed: FEED,
        },
        author: { "@type": "Person", name: "Grant Giles", url: `${location.origin}/about/` },
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.dataset.epSchema = "";
    tag.textContent = JSON.stringify(data);
    document.head.appendChild(tag);
}

export default function episodePlayer() {
    const page = document.querySelector("[data-episode]");
    if (!page) return;

    const title = page.dataset.epTitle || document.title;
    const band = page.querySelector("[data-ep-wave]");
    if (band) drawWave(band, title, 110);
    page.querySelectorAll("[data-ep-card]").forEach((card) => {
        const svg = card.querySelector(".gg-ep__more-wave");
        if (svg) drawWave(svg, card.dataset.epCard, 60);
    });

    const notes = page.querySelector("[data-ep-notes]");
    const slot = page.querySelector("[data-ep-player]");
    // Buzzsprout's script embed (most posts), or its bare iframe (a few)
    const embed = notes && notes.querySelector(`[id^="buzzsprout-player-"], iframe[src*="buzzsprout.com/${SHOW}/"]`);
    const id = embed && ((embed.id || "").match(/(\d+)$/) || (embed.src || "").match(/\/(\d+)-/) || [])[1];
    // Otherwise a plain audio file (guest spots on shows with no Spotify
    // page): an <audio src>, or a link straight to an .mp3 / .m4a
    const file =
        !id &&
        notes &&
        (notes.querySelector("audio[src]") ||
            [...notes.querySelectorAll("a[href]")].find((a) => /\.(mp3|m4a)$/i.test(a.pathname)));
    if (!slot || !(id || file)) {
        writeSchema(page);
        return;
    }

    if (file) {
        // A link on a line of its own goes with its paragraph
        const parent = file.parentElement;
        const lone = file.tagName === "A" && parent.tagName === "P" && parent.textContent.trim() === file.textContent.trim();
        const target = lone ? parent : file;
        const marker = document.createComment("audio");
        target.replaceWith(marker);
        buildPlayer(slot, file.src || file.href, title, page.hasAttribute("data-ep-guest") ? undefined : "The Roaring Heads");
        slot.hidden = false;
        slot.addEventListener("ep:fail", () => {
            slot.hidden = true;
            marker.replaceWith(target);
        });
        writeSchema(page);
        return;
    }

    // Lift the embed out (keeping it to put back on failure) and its
    // script tag, which has already run.
    const marker = document.createComment("buzzsprout");
    embed.replaceWith(marker);
    const script = marker.nextElementSibling;
    if (script && script.tagName === "SCRIPT" && /buzzsprout/.test(script.src)) script.remove();
    dropListenLine(notes);

    const player = buildPlayer(slot, `https://www.buzzsprout.com/${SHOW}/${id}.mp3`, title, "The Roaring Heads");
    slot.hidden = false;
    slot.addEventListener("ep:fail", () => {
        slot.hidden = true;
        marker.replaceWith(embed);
    });

    const fill = (index) => {
        const [num, dur] = index[id] || [];
        const numEl = page.querySelector("[data-ep-num]");
        const lenEl = page.querySelector("[data-ep-length]");
        if (num && numEl) {
            numEl.textContent = ` · Episode ${num}`;
            numEl.hidden = false;
        }
        if (dur) {
            player.setDuration(dur);
            if (lenEl) {
                lenEl.textContent = ` · ${Math.round(dur / 60)} min`;
                lenEl.hidden = false;
            }
        }
    };

    feedIndex()
        .then((index) => {
            fill(index);
            const [num, dur] = index[id] || [];
            writeSchema(page, { id, num, dur });
        })
        .catch(() => {
            // Feed unreachable: the player still works, just without number and length.
            writeSchema(page, { id });
        });
}
